import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  CreditCard,
  TrendingUp,
  Calendar,
  Check,
  ArrowUpCircle,
} from "lucide-react";

export function Subscription() {
  // TODO: Replace with actual API call when backend is ready
  const { data, isLoading, error } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => {
      // Mock data for now - will be replaced with api.getSubscription()
      return {
        subscription: {
          tier: "pro",
          status: "active",
          billing_cycle: "monthly",
          next_billing_date: "2026-02-14",
          amount: 299,
          currency: "USD",
        },
        usage: {
          active_bobs: 8,
          max_bobs: 20,
          queries_this_month: 1250,
          storage_gb: 5.2,
        },
      };
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400">Loading subscription...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-red-400">
          Error loading subscription: {(error as Error).message}
        </div>
      </div>
    );
  }

  const subscription = data?.subscription;
  const usage = data?.usage;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-100">Subscription</h1>
        <p className="mt-1 text-sm text-slate-400">
          Manage your plan and view usage
        </p>
      </div>

      {/* Current Plan */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-100 mb-2">
              Current Plan
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-blue-400 capitalize">
                {subscription?.tier}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 capitalize">
                {subscription?.status}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-slate-100">
              ${subscription?.amount}
              <span className="text-lg text-slate-400">/mo</span>
            </div>
            <div className="text-sm text-slate-400 mt-1">
              Billed {subscription?.billing_cycle}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-700">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
            <Calendar className="h-4 w-4" />
            Next billing date:{" "}
            {subscription &&
              new Date(subscription.next_billing_date).toLocaleDateString()}
          </div>

          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors">
              <ArrowUpCircle className="h-4 w-4" />
              Upgrade Plan
            </button>
            <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors">
              <CreditCard className="h-4 w-4" />
              Update Payment Method
            </button>
          </div>
        </div>
      </div>

      {/* Usage Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">BoBs Used</span>
            <TrendingUp className="h-4 w-4 text-blue-400" />
          </div>
          <div className="text-2xl font-bold text-slate-100">
            {usage?.active_bobs} / {usage?.max_bobs}
          </div>
          <div className="mt-2 w-full bg-slate-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full"
              style={{
                width: `${((usage?.active_bobs || 0) / (usage?.max_bobs || 1)) * 100}%`,
              }}
            />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Queries This Month</span>
            <TrendingUp className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-slate-100">
            {usage?.queries_this_month?.toLocaleString()}
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Unlimited on Pro plan
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Storage Used</span>
            <TrendingUp className="h-4 w-4 text-purple-400" />
          </div>
          <div className="text-2xl font-bold text-slate-100">
            {usage?.storage_gb?.toFixed(1)} GB
          </div>
          <div className="mt-2 text-xs text-slate-500">50 GB included</div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Support</span>
            <Check className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="text-2xl font-bold text-slate-100">Priority</div>
          <div className="mt-2 text-xs text-slate-500">24/7 response</div>
        </div>
      </div>

      {/* Available Plans */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
        <h2 className="text-xl font-semibold text-slate-100 mb-6">
          Available Plans
        </h2>

        <div className="grid gap-4 md:grid-cols-3">
          {[
            {
              tier: "starter",
              name: "Starter",
              price: 99,
              bobs: 5,
              features: [
                "Basic support",
                "Email notifications",
                "Standard SLA",
                "10 GB storage",
              ],
              current: false,
            },
            {
              tier: "pro",
              name: "Professional",
              price: 299,
              bobs: 20,
              features: [
                "Priority support",
                "Real-time alerts",
                "24/7 SLA",
                "50 GB storage",
                "API access",
              ],
              current: true,
            },
            {
              tier: "enterprise",
              name: "Enterprise",
              price: null,
              bobs: "Unlimited",
              features: [
                "Dedicated support",
                "Custom integrations",
                "99.9% uptime SLA",
                "Unlimited storage",
                "White-label options",
              ],
              current: false,
            },
          ].map((plan) => (
            <div
              key={plan.tier}
              className={`p-6 rounded-lg border-2 transition-all ${
                plan.current
                  ? "border-blue-500 bg-blue-500/5"
                  : "border-slate-600 hover:border-slate-500"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-100">
                  {plan.name}
                </h3>
                {plan.current && (
                  <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    Current
                  </span>
                )}
              </div>

              <div className="mb-4">
                <span className="text-3xl font-bold text-slate-100">
                  {plan.price ? `$${plan.price}` : "Custom"}
                </span>
                {plan.price && <span className="text-slate-400">/month</span>}
              </div>

              <div className="mb-6">
                <span className="text-sm text-emerald-400">
                  Up to {plan.bobs}{" "}
                  {typeof plan.bobs === "number" ? "BoBs" : ""}
                </span>
              </div>

              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, idx) => (
                  <li
                    key={idx}
                    className="flex items-center gap-2 text-sm text-slate-300"
                  >
                    <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {!plan.current && (
                <button
                  className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                    plan.tier === "enterprise"
                      ? "bg-slate-700 hover:bg-slate-600 text-slate-300"
                      : "bg-blue-500 hover:bg-blue-600 text-white"
                  }`}
                >
                  {plan.tier === "enterprise" ? "Contact Sales" : "Upgrade"}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
